import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MesRemboursementsService } from '@features/mes-remboursements/services/mes-remboursemens.service';
import { catchError, concat, map, of, switchMap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { selectAllDispositifs, selectAllSirets } from '@features/referentiel/features';
import { selectFilteredAndLimitedEntitesSiretFromQuery, selectMesRemboursementsRechercheFormCategorie, selectMesRemboursementsRechercheFormValues } from '@features/mes-remboursements/store/search/search.selectors';
import { MesRemboursementsActions } from '@features/mes-remboursements/store/remboursements/mes-remboursements.actions';
import { MesRemboursementsSearchFormActions } from '@features/mes-remboursements/store/search/search.actions';
import { MesRemboursementsFunctions } from '@features/mes-remboursements/fonctions/mes-remboursements-fonctions';
import { selectMesRemboursementsTotalElements } from '../remboursements/mes-remboursements.selectors';
import { ToastService } from '@shared/services/toast/toast.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { createExportEffectFactory } from '@shared/functions/common/export-effects.utils';

function buildSearchCriteria(
  form: any,
  sirets: string[],
  categorieDossier: any
) {
  const siretForm = form.siret ? [form.siret] : sirets;
  return MesRemboursementsFunctions.searchFormToDossierSearchCriteria(
    siretForm,
    form,
    categorieDossier
  );
}

function fetchDossiers(
  dossierSearchCriteria: any,
  mesRemboursementsServices: MesRemboursementsService
) {
  return concat(
    of(MesRemboursementsActions.updateLoading({ isLoading: true })),
    mesRemboursementsServices.getDossiers(dossierSearchCriteria).pipe(
      map((dossiers) =>
        MesRemboursementsActions.getDossiersBySearchCriteriaSuccess({ dossiers })
      ),
      catchError((error) =>
        of(
          MesRemboursementsActions.getDossiersBySearchCriteriaFailure({
            errorMessage: error.error,
          })
        )
      )
    ),
    of(MesRemboursementsActions.updateLoading({ isLoading: false }))
  );
}

export const loadDossiersRemboursement$ = createEffect(
  (
    actions$ = inject(Actions),
    mesRemboursementsServices = inject(MesRemboursementsService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesRemboursementsSearchFormActions.updateSearchForm),
      withLatestFrom(
        store.select(selectAllSirets),
        store.select(selectMesRemboursementsRechercheFormCategorie)
      ),
      switchMap(([action, sirets, categorieDossier]) => {
        const criteria = buildSearchCriteria(
          action.form,
          sirets,
          categorieDossier
        );
        return sirets.length > 0
          ? fetchDossiers(criteria, mesRemboursementsServices)
          : of(MesRemboursementsActions.noActions());
      })
    );
  },
  { functional: true },
);

export const resetDossiersRemboursement$ = createEffect(
  (
    actions$ = inject(Actions),
    mesRemboursementsServices = inject(MesRemboursementsService),
    store = inject(Store),
  ) => {
    return actions$.pipe(
      ofType(MesRemboursementsSearchFormActions.resetSearchForm, MesRemboursementsSearchFormActions.getSearchForm),
      withLatestFrom(
        store.select(selectMesRemboursementsRechercheFormValues),
        store.select(selectAllSirets),
        store.select(selectMesRemboursementsRechercheFormCategorie),
      ),
      switchMap(([, form, sirets, categorieDossier]) => {
        const criteria = buildSearchCriteria(form, sirets, categorieDossier);
        return sirets.length > 0
          ? fetchDossiers(criteria, mesRemboursementsServices)
          : of(MesRemboursementsActions.noActions());
      })
    );
  },
  { functional: true },
);

export const searchOrganismeFormationFromQuerySiret$ = createEffect(
  (actions$ = inject(Actions), store = inject(Store)) => {
    return actions$.pipe(
      ofType(MesRemboursementsSearchFormActions.searchSiretFromQuery),
      switchMap(() =>
        store.select(selectFilteredAndLimitedEntitesSiretFromQuery(10)).pipe(
          map((result) => {
            if (result.length === 0) {
              return MesRemboursementsSearchFormActions.searchSiretFromQueryFailure({
                validationErrors: { siret: { notFound: true } },
              });
            }
            return MesRemboursementsSearchFormActions.searchSiretFromQuerySucces();
          }),
        ),
      ),
    );
  },
  { functional: true },
);

export const exportDossiersRemboursement$ = createExportEffectFactory({
  trigger: MesRemboursementsSearchFormActions.exportMesRemboursements,
  success: MesRemboursementsSearchFormActions.exportMesRemboursementsSucces,
  failure: MesRemboursementsSearchFormActions.exportMesRemboursementsFailure,
  selectors: () => {
    const store = inject(Store);
    return {
      sirets$: store.select(selectAllSirets),
      form$: store.select(selectMesRemboursementsRechercheFormValues),
      categorie$: store.select(selectMesRemboursementsRechercheFormCategorie),
      dispositifs$: store.select(selectAllDispositifs),
      total$: store.select(selectMesRemboursementsTotalElements),
    };
  },
  criteriaBuilder: ({ sirets, form, categorieDossier, dispositifs }) =>
    MesRemboursementsFunctions.searchFormToRemboursementRechercheExportRequest(sirets, form, categorieDossier, dispositifs),
  call: (svc, criteria) => svc.getDossiersExport(criteria),
  injectService: () => inject(MesRemboursementsService),
  loading: {
    start: MesRemboursementsActions.updateExportLoading,
    stop: MesRemboursementsActions.updateExportLoading,
  },
});

export const showExportErrorToast$ = createEffect(
  (
    actions$ = inject(Actions),
    toastService = inject(ToastService),
    translateService = inject(TranslateService),
  ) => {
    return actions$.pipe(
      ofType(MesRemboursementsSearchFormActions.exportMesRemboursementsFailure),
      map((action) => {
        toastService.showErrorToastWithButtonAndWithoutTitle(action.error ?? translateService.instant('toast.export.error'));
        return MesRemboursementsActions.noActions();
      })
    );
  },
  { functional: true },
);